import React, { useState, useCallback, useEffect, useRef } from 'react';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';

const LocationMapPicker = ({ onLocationSelect, initialPosition, addressToGeocode }) => {
  const [markerPosition, setMarkerPosition] = useState(
    initialPosition || { lat: 35.6762, lng: 139.6503 }
  );
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [isGeocodingAddress, setIsGeocodingAddress] = useState(false);

  const lastFormattedAddressRef = useRef('');
  const lastGeocodedInputRef = useRef('');

  const normalizeAddress = (addr) => {
    if (!addr) return '';
    return addr.toLowerCase().trim().replace(/\s+/g, ' ');
  };

  // Forward geocode using Google Maps Geocoder
  const forwardGeocode = useCallback(async (addressString) => {
    if (!addressString || addressString.trim() === '') return;

    setIsGeocodingAddress(true);

    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();

      await new Promise((resolve) => {
        geocoder.geocode({ address: addressString }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const location = results[0].geometry.location;
            const lat = location.lat();
            const lng = location.lng();
            const formattedAddress = results[0].formatted_address;

            setMarkerPosition({ lat, lng });
            setAddress(formattedAddress);
            lastGeocodedInputRef.current = addressString;
            lastFormattedAddressRef.current = formattedAddress;

            let prefecture = '';
            let city = '';
            for (const component of results[0].address_components) {
              if (component.types.includes('administrative_area_level_1')) prefecture = component.long_name;
              if (component.types.includes('locality')) city = component.long_name;
            }

            if (onLocationSelect) {
              onLocationSelect({ lat, lng, address: formattedAddress, prefecture, city, placeId: results[0].place_id, skipGeocoding: true });
            }
          } else {
            console.warn('Google Geocoding failed:', status);
          }
          setIsGeocodingAddress(false);
          resolve();
        });
      });
    } else {
      setIsGeocodingAddress(false);
    }
  }, [onLocationSelect]);

  // Reverse geocode using Google Maps Geocoder
  const reverseGeocode = useCallback(async (lat, lng) => {
    setLoading(true);
    try {
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();

        await new Promise((resolve) => {
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const formattedAddress = results[0].formatted_address;
              setAddress(formattedAddress);
              lastFormattedAddressRef.current = formattedAddress;
              lastGeocodedInputRef.current = formattedAddress;

              let prefecture = '';
              let city = '';
              for (const component of results[0].address_components) {
                if (component.types.includes('administrative_area_level_1')) prefecture = component.long_name;
                if (component.types.includes('locality')) city = component.long_name;
              }

              if (onLocationSelect) {
                onLocationSelect({ lat, lng, address: formattedAddress, prefecture, city, placeId: results[0].place_id });
              }
            } else {
              setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
            }
            setLoading(false);
            resolve();
          });
        });
      } else {
        setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        setLoading(false);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setAddress('Unable to get address');
      setLoading(false);
    }
  }, [onLocationSelect]);

  const handleMapClick = useCallback((e) => {
    if (e.detail && e.detail.latLng) {
      const { lat, lng } = e.detail.latLng;
      setMarkerPosition({ lat, lng });
      reverseGeocode(lat, lng);
    }
  }, [reverseGeocode]);

  useEffect(() => {
    if (markerPosition) {
      reverseGeocode(markerPosition.lat, markerPosition.lng);
    }
  }, []); // Only run once on mount

  useEffect(() => {
    if (!addressToGeocode || addressToGeocode.trim() === '') return;

    const normalizedInput = normalizeAddress(addressToGeocode);
    const normalizedLastFormatted = normalizeAddress(lastFormattedAddressRef.current);
    const normalizedLastGeocoded = normalizeAddress(lastGeocodedInputRef.current);

    if (isGeocodingAddress || loading || normalizedInput === normalizedLastFormatted || normalizedInput === normalizedLastGeocoded) return;

    const strippedInput = addressToGeocode.replace(/[,\s]/g, '').toLowerCase();
    const strippedFormatted = lastFormattedAddressRef.current.replace(/[,\s]/g, '').toLowerCase();
    if (strippedInput === strippedFormatted) return;

    const timeoutId = setTimeout(() => {
      forwardGeocode(addressToGeocode);
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [addressToGeocode, isGeocodingAddress, loading, forwardGeocode]);

  return (
    <div className="w-full">
      <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-slate-700 mb-1">
          <strong>Two ways to set location:</strong>
        </p>
        <p className="text-xs text-slate-600">
          Type an address in the field below and the map will update automatically, or click on the map to drop a pin and the address fields will be filled.
        </p>
        {isGeocodingAddress && (
          <p className="text-xs text-blue-600 mt-2">Finding location on map...</p>
        )}
      </div>

      <div className="border border-slate-300 rounded-lg overflow-hidden shadow-sm">
        <Map
          style={{ width: '100%', height: '450px' }}
          defaultCenter={markerPosition}
          defaultZoom={15}
          onClick={handleMapClick}
          gestureHandling="greedy"
        >
          <Marker position={markerPosition} />
        </Map>
      </div>

      {address && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-sm font-medium text-slate-700 mb-2">Selected Location:</p>
          <p className="text-sm text-slate-600 mb-1">
            <strong>Address:</strong> {loading ? 'Loading...' : address}
          </p>
          <p className="text-xs text-slate-500">
            <strong>Coordinates:</strong> {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationMapPicker;
